import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import onboardingService from '../services/onboardingService';
import { draftSliceForStep, sessionToClientState } from '../services/onboardingMappers';
import { getStepById, LAST_STEP_ID } from '../utils/onboardingSteps.config';
import { createFreshState, createInitialDraft } from '../utils/constants';
import { applyClearedDraftSlice } from '../utils/draftReset';
import { useWizardSessionStorage } from './useWizardSessionStorage';
import { canNavigateToStep, clampStep, getResumeStep } from '../utils/stepNavigation';

export function applyClientState(session, setters) {
  const client = sessionToClientState(session);
  setters.setSessionId(client.sessionId);
  setters.setOrganisationId(client.organisationId);
  setters.setProfileId(client.profileId);
  setters.setCurrentStep(clampStep(client.currentStep));
  setters.setSteps(client.steps);
  setters.setDraftState(client.draft);
  return client;
}

export function useWizardSession({
  sessionIdRef,
  draftRef,
  currentStepRef,
  stepsRef,
  setSessionIdState,
  setOrganisationId,
  setProfileId,
  setCurrentStep,
  setSteps,
  setDraftState,
  setSaveError,
  setIsInitializing,
  setShowResumeBanner,
  bannerDismissed,
}) {
  const { getSessionId, setSessionId, clearSessionId, clearLegacyDraft } = useWizardSessionStorage();
  const [searchParams] = useSearchParams();
  const initialized = useRef(false);
  const saveTimeout = useRef(null);

  const hydrateFromSession = useCallback(
    (session) => {
      return applyClientState(session, {
        setSessionId: (id) => {
          setSessionIdState(id);
          setSessionId(id);
        },
        setOrganisationId,
        setProfileId,
        setCurrentStep,
        setSteps,
        setDraftState,
      });
    },
    [setSessionId, setSessionIdState, setOrganisationId, setProfileId, setCurrentStep, setSteps, setDraftState]
  );

  const initSession = useCallback(async () => {
    setIsInitializing(true);
    setSaveError(null);
    clearLegacyDraft();

    try {
      const cachedId = getSessionId();
      if (cachedId) {
        const getRes = await onboardingService.getSession(cachedId);
        if (getRes.ok && getRes.data) {
          hydrateFromSession(getRes.data);
          setIsInitializing(false);
          return;
        }
        clearSessionId();
      }

      const createRes = await onboardingService.createOrResumeSession();
      if (!createRes.ok || !createRes.data) {
        setSaveError('Could not start setup wizard. Please refresh the page.');
        setIsInitializing(false);
        return;
      }

      hydrateFromSession(createRes.data);
    } catch {
      setSaveError('Could not connect to setup wizard. Please try again.');
    } finally {
      setIsInitializing(false);
    }
  }, [clearLegacyDraft, clearSessionId, getSessionId, hydrateFromSession, setIsInitializing, setSaveError]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    void initSession().then(() => {
      const urlStep = parseInt(searchParams.get('step') || '', 10);
      if (!Number.isNaN(urlStep)) {
        const clamped = clampStep(urlStep);
        setCurrentStep((prev) => {
          if (canNavigateToStep(clamped, stepsRef.current)) return clamped;
          return prev;
        });
      }
      const resume = getResumeStep(stepsRef.current);
      if (resume > 1 && resume < LAST_STEP_ID) {
        setShowResumeBanner(!bannerDismissed);
      }
    });
  }, [initSession, searchParams, bannerDismissed, setCurrentStep, setShowResumeBanner, stepsRef]);

  const flushStepDraft = useCallback(
    async (stepKey, draftPayload) => {
      const sid = sessionIdRef.current;
      if (!sid) return false;
      const body = draftSliceForStep(stepKey, draftPayload);
      const res = await onboardingService.putStepDraft(sid, stepKey, body);
      if (!res.ok) {
        setSaveError('Failed to save progress. Please try again.');
        return false;
      }
      return true;
    },
    [sessionIdRef, setSaveError]
  );

  const debouncedPersistDraft = useCallback(
    (nextDraft, step) => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(() => {
        const config = getStepById(step);
        if (!config || !sessionIdRef.current) return;
        void flushStepDraft(config.key, nextDraft);
      }, 400);
    },
    [flushStepDraft, sessionIdRef]
  );

  const setDraft = useCallback(
    (updater) => {
      setDraftState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
        const key = getStepById(currentStepRef.current)?.key;
        const nextSteps = { ...stepsRef.current };
        if (key && nextSteps[key] === 'pending') {
          nextSteps[key] = 'in_progress';
        }
        setSteps(nextSteps);
        debouncedPersistDraft(next, currentStepRef.current);
        return next;
      });
    },
    [currentStepRef, debouncedPersistDraft, setDraftState, setSteps, stepsRef]
  );

  const syncSessionAfterMutation = useCallback(
    (session) => {
      hydrateFromSession(session);
    },
    [hydrateFromSession]
  );

  const resetWizardSession = useCallback(async () => {
    if (sessionIdRef.current) {
      await onboardingService.deleteSession(sessionIdRef.current);
    }
    clearSessionId();
    const fresh = createFreshState();
    setSteps(fresh.steps);
    setDraftState(fresh.draft);
    setOrganisationId(null);
    setProfileId(null);
    setCurrentStep(1);

    const createRes = await onboardingService.createOrResumeSession();
    if (createRes.ok && createRes.data) {
      hydrateFromSession(createRes.data);
    }
  }, [
    clearSessionId,
    hydrateFromSession,
    sessionIdRef,
    setCurrentStep,
    setDraftState,
    setOrganisationId,
    setProfileId,
    setSteps,
  ]);

  const clearSaveTimeout = useCallback(() => {
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
      saveTimeout.current = null;
    }
  }, []);

  const clearCurrentStep = useCallback(
    async (stepId) => {
      const config = getStepById(stepId);
      const sid = sessionIdRef.current;
      if (!config || !sid) return { ok: false };

      clearSaveTimeout();

      const res = await onboardingService.resetStepDraft(sid, config.key);
      if (!res.ok) {
        setSaveError('Failed to clear this step. Please try again.');
        return { ok: false };
      }

      const session = res.data?.session;
      if (session) {
        hydrateFromSession(session);
      } else {
        setDraftState((prev) => applyClearedDraftSlice(prev, config.key));
        setSteps((prev) => ({ ...prev, [config.key]: 'pending' }));
      }

      return { ok: true };
    },
    [
      clearSaveTimeout,
      hydrateFromSession,
      sessionIdRef,
      setDraftState,
      setSaveError,
      setSteps,
    ]
  );

  return {
    hydrateFromSession,
    flushStepDraft,
    setDraft,
    syncSessionAfterMutation,
    resetWizardSession,
    clearCurrentStep,
    clearSaveTimeout,
    clearSessionId,
  };
}
